import type { Question } from '../types';
import { buildPlatformScope, type PlatformScope } from '../platformScope';
import { getSkillSectionLabel } from '../labels';

export type DiagnosisExamType = 'tcf' | 'ielts' | 'toefl' | string;
export type DiagnosisLevel = 'A1' | 'A2' | 'B1' | 'B2';

export interface DiagnosisAnswerInput {
  questionId: string;
  isCorrect: boolean;
  timeSpent: number;
}

export interface DiagnosisWeakArea {
  id: string;
  label: string;
  sectionId?: string;
  tag?: string;
  incorrect: number;
  total: number;
  accuracy: number;
}

export interface DiagnosisTrainingRecommendation {
  title: string;
  sectionId?: string;
  tag?: string;
  level: DiagnosisLevel;
  questionCount: number;
  reason: string;
  platformScope: PlatformScope | null;
}

export interface QuickDiagnosisReport {
  id: string;
  examType: string;
  platformScope: PlatformScope | null;
  createdAt: number;
  totalQuestions: number;
  correctCount: number;
  accuracy: number;
  level: {
    cefr: DiagnosisLevel;
    display: string;
    description: string;
  };
  weakAreas: DiagnosisWeakArea[];
  recommendedDifficulty: DiagnosisLevel;
  todayTraining: DiagnosisTrainingRecommendation;
}

export interface BuildQuickDiagnosisReportInput {
  examType: DiagnosisExamType;
  languageId?: string | null;
  questions: Question[];
  answers: DiagnosisAnswerInput[];
  now?: number;
}

const TAG_LABELS: Record<string, string> = {
  detail: '细节理解',
  inference: '推断理解',
  vocabulary: '词汇理解',
  'verb-tense': '动词时态',
  tense: '时态语法',
  grammar: '语法规则',
  'main-idea': '主旨理解',
  main_idea: '主旨理解',
  cohesion: '逻辑衔接',
};

const LEVEL_DESCRIPTIONS: Record<DiagnosisLevel, string> = {
  A1: '适合从基础题型和核心词汇开始建立稳定正确率。',
  A2: '已经具备基础理解能力，适合补齐常见题型和基础弱项。',
  B1: '可以进入中级综合训练，重点提升定位、推断和细节稳定性。',
  B2: '适合挑战更接近考试节奏的综合训练和限时练习。',
};

function normalizeExamType(examType: DiagnosisExamType): string {
  return String(examType || 'unknown').toLowerCase();
}

function sectionLabel(sectionId: string | undefined): string {
  if (!sectionId) return '综合能力';
  return getSkillSectionLabel(sectionId);
}

function tagLabel(tag: string): string {
  return TAG_LABELS[tag] ?? tag;
}

export function mapCorrectRateToLevel(
  examType: DiagnosisExamType,
  correctRate: number
): QuickDiagnosisReport['level'] {
  const safeRate = Number.isFinite(correctRate) ? Math.max(0, Math.min(1, correctRate)) : 0;
  const cefr: DiagnosisLevel =
    safeRate < 0.25 ? 'A1' : safeRate < 0.6 ? 'A2' : safeRate < 0.82 ? 'B1' : 'B2';
  const exam = normalizeExamType(examType);

  const scoreText: Record<DiagnosisLevel, string> =
    exam === 'ielts'
      ? { A1: 'IELTS 3.5-4.0', A2: 'IELTS 4.5-5.0', B1: 'IELTS 5.5-6.0', B2: 'IELTS 6.5+' }
      : exam === 'toefl'
        ? { A1: 'TOEFL 30-45', A2: 'TOEFL 46-59', B1: 'TOEFL 60-78', B2: 'TOEFL 79+' }
        : exam === 'tcf'
          ? { A1: 'TCF A1 附近', A2: 'TCF A2 附近', B1: 'TCF B1 附近', B2: 'TCF B2 附近' }
          : { A1: 'CEFR A1 附近', A2: 'CEFR A2 附近', B1: 'CEFR B1 附近', B2: 'CEFR B2 附近' };

  return {
    cefr,
    display: `${scoreText[cefr]} / ${cefr}`,
    description: LEVEL_DESCRIPTIONS[cefr],
  };
}

export function getWeakAreas(
  questions: Question[],
  answers: DiagnosisAnswerInput[]
): DiagnosisWeakArea[] {
  const questionById = new Map(questions.map((question) => [question.id, question]));
  const stats = new Map<
    string,
    { label: string; sectionId?: string; tag?: string; incorrect: number; total: number }
  >();

  const add = (
    key: string,
    label: string,
    answer: DiagnosisAnswerInput,
    meta: { sectionId?: string; tag?: string }
  ) => {
    const current = stats.get(key) ?? {
      label,
      sectionId: meta.sectionId,
      tag: meta.tag,
      incorrect: 0,
      total: 0,
    };
    current.total += 1;
    if (!answer.isCorrect) current.incorrect += 1;
    stats.set(key, current);
  };

  for (const answer of answers) {
    const question = questionById.get(answer.questionId);
    if (!question) continue;
    const sectionId = question.section || question.type;
    add(`section:${sectionId}`, sectionLabel(sectionId), answer, { sectionId });
    for (const tag of question.tags ?? []) {
      add(`tag:${tag}`, tagLabel(tag), answer, { tag });
    }
  }

  return [...stats.entries()]
    .map(([id, stat]) => ({
      id,
      label: stat.label,
      sectionId: stat.sectionId,
      tag: stat.tag,
      incorrect: stat.incorrect,
      total: stat.total,
      accuracy: stat.total > 0 ? (stat.total - stat.incorrect) / stat.total : 0,
    }))
    .filter((area) => area.total > 0 && area.incorrect > 0)
    .sort((a, b) => {
      const aMissRate = a.incorrect / a.total;
      const bMissRate = b.incorrect / b.total;
      if (bMissRate !== aMissRate) return bMissRate - aMissRate;
      if (b.incorrect !== a.incorrect) return b.incorrect - a.incorrect;
      return b.total - a.total;
    })
    .slice(0, 4);
}

export function getTodayTraining(
  weakAreas: DiagnosisWeakArea[],
  level: DiagnosisLevel,
  reportScope: PlatformScope | null
): DiagnosisTrainingRecommendation {
  const weakest = weakAreas[0];
  const label = weakest?.label ?? '综合能力';
  const sectionId = weakest?.sectionId;

  return {
    title: `练习 12 道${label}仿真题`,
    sectionId,
    tag: weakest?.tag,
    level,
    questionCount: 12,
    reason: weakest
      ? `${label}是本次诊断中最需要优先巩固的部分。`
      : '本次诊断没有明显短板，建议进行综合巩固训练。',
    platformScope: reportScope
      ? buildPlatformScope({
          languageId: reportScope.languageId,
          examType: reportScope.examType,
          skillId: sectionId,
        })
      : null,
  };
}

export function buildQuickDiagnosisReport(
  input: BuildQuickDiagnosisReportInput
): QuickDiagnosisReport {
  const totalQuestions = input.questions.length;
  const correctCount = input.answers.filter((answer) => answer.isCorrect).length;
  const accuracy = totalQuestions > 0 ? correctCount / totalQuestions : 0;
  const level = mapCorrectRateToLevel(input.examType, accuracy);
  const weakAreas = getWeakAreas(input.questions, input.answers);
  const platformScope = buildPlatformScope({
    languageId: input.languageId,
    examType: input.examType,
  });
  const todayTraining = getTodayTraining(weakAreas, level.cefr, platformScope);

  return {
    id: `diagnosis-${input.now ?? Date.now()}`,
    examType: normalizeExamType(input.examType),
    platformScope,
    createdAt: input.now ?? Date.now(),
    totalQuestions,
    correctCount,
    accuracy,
    level,
    weakAreas,
    recommendedDifficulty: level.cefr,
    todayTraining,
  };
}
